
type ValidationResult = { value: any; status: "valid" | "invalid"; message?: string };

export const normalizeHeader = (header: string): string => {
	return header
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^\w\s]/gi, "")
		.trim()
		.toLowerCase()
		.replace(/\s+/g, "");
};

export const validateHeaders = (
	headers: string[],
	requiredFields: Record<string, string[]>
): { valid: boolean; missing: string[]; matched: Record<string, string> } => {
	const normalizedHeadersMap = headers.reduce((acc, h) => {
		acc[normalizeHeader(h)] = h;
		return acc;
	}, {} as Record<string, string>);

	const missing: string[] = [];
	const matched: Record<string, string> = {};

	for (const [key, aliases] of Object.entries(requiredFields)) {
		const found = aliases.find((alias) => normalizedHeadersMap.hasOwnProperty(normalizeHeader(alias)));
		if (found) {
			matched[key] = found;
		} else {
			missing.push(key);
		}
	}

	return {
		valid: missing.length === 0,
		missing,
		matched,
	};
};

export const cleanText = (value: string): string => String(value).trim().replace(/\s+/g, " ");

export const removeMask = (value: string): string => String(value).replace(/[^\d]+/g, "");

export const parseCurrency = (value: string): number => {
	const cleaned = value
		.replace(/[^\d,.-]/g, "")
		.replace(/\./g, "")
		.replace(",", ".");
	const parsed = parseFloat(cleaned);
	return isNaN(parsed) ? 0 : parsed;
};

export const parseDate = (value: any): string => {
	if (!value) return "";

	// 📌 Caso venha como número (serial do Excel)
	if (typeof value === "number") {
		const excelEpoch = new Date(1900, 0, 1);
		const date = new Date(excelEpoch.getTime() + (value - 2) * 86400000); // corrigido -2
		return date.toISOString().split("T")[0];
	}

	if (typeof value === "string") {
		const cleaned = value.trim();

		// ISO direto
		if (/^\d{4}-\d{2}-\d{2}/.test(cleaned)) {
			const isoParsed = new Date(cleaned);
			return isNaN(isoParsed.getTime()) ? "" : isoParsed.toISOString().split("T")[0];
		}

		// Formatos comuns brasileiros
		const parts = cleaned.split(/[\/\-]/);
		if (parts.length === 3) {
			const [dia, mes, ano] = parts;
			if (ano.length === 4) {
				const date = new Date(+ano, +mes - 1, +dia);
				return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
			}
		}
	}

	return "";
};

export const normalizeCpfCnpj = (value: string): { value: string; status: "valid" | "invalid"; message: string } => {
	const digits = removeMask(value);

	if (digits.length <= 8) {
		return { value: digits, status: "invalid", message: "Documento com poucos dígitos" };
	}

	if (digits.length < 11) {
		return { value: digits.padStart(11, "0"), status: "valid", message: "" };
	}

	if (digits.length === 11) {
		return { value: digits, status: "valid", message: "" }; // CPF
	}

	if (digits.length < 14) {
		return { value: digits.padStart(14, "0"), status: "valid", message: "" };
	}

	if (digits.length === 14) {
		return { value: digits, status: "valid", message: "" }; // CNPJ
	}

	return { value: digits, status: "invalid", message: "Documento com muitos dígitos" };
};

export const validateEmail = (value: any): ValidationResult => {
	if (typeof value !== "string") {
		return { value, status: "invalid", message: "Email inválido" };
	}

	const str = value.trim();

	if (!str) {
		return {
			value,
			status: "invalid",
			message: "Email obrigatório",
		};
	}

	const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);
	return {
		value: str,
		status: isValidEmail ? "valid" : "invalid",
		message: isValidEmail ? "" : "Email inválido",
	};
};

export const validateCep = (value: any): ValidationResult => {
	const cleaned = String(value ?? "").replace(/[^\d]/g, "");

	if (!cleaned) {
		return {
			value,
			status: "invalid",
			message: "CEP obrigatório",
		};
	}

	if (cleaned.length !== 8) {
		return {
			value: cleaned,
			status: "invalid",
			message: "CEP inválido",
		};
	}

	return {
		value: cleaned,
		status: "valid",
		message: "",
	};
};

export const validatePhone = (value: any): ValidationResult => {
	let digits = String(value ?? "")
		.replace(/[^\d]/g, "") // remove tudo que não for número
		.replace(/^55/, ""); // remove DDI +55 se tiver

	if (!digits) {
		return {
			value,
			status: "invalid",
			message: "Telefone obrigatório",
		};
	}

	if (digits.length < 10 || digits.length > 11) {
		return {
			value: digits,
			status: "invalid",
			message: "Telefone deve ter 10 ou 11 dígitos",
		};
	}

	// Caso 10 dígitos
	if (digits.length === 10) {
		const thirdDigit = Number(digits[2]);

		if (thirdDigit >= 1 && thirdDigit <= 6) {
			return {
				value: digits,
				status: "valid",
				message: "",
			};
		}

		if (thirdDigit > 6) {
			// Inserir 9 após o segundo dígito
			const corrected = digits.slice(0, 2) + "9" + digits.slice(2);
			return {
				value: corrected,
				status: "valid",
				message: "",
			};
		}

		return {
			value: digits,
			status: "invalid",
			message: "Telefone fixo inválido",
		};
	}

	// Caso 11 dígitos
	if (digits.length === 11) {
		const thirdDigit = Number(digits[2]);

		if (thirdDigit >= 7 && thirdDigit <= 9) {
			return {
				value: digits,
				status: "valid",
				message: "",
			};
		}

		return {
			value: digits,
			status: "invalid",
			message: "Celular inválido",
		};
	}

	// fallback
	return {
		value: digits,
		status: "invalid",
		message: "Telefone inválido",
	};
};

export const validateDate = (value: any): ValidationResult => {
	if (!value || typeof value !== "string") {
		return { value: "", status: "invalid", message: "Data inválida ou vazia" };
	}

	const raw = value.trim();

	// Ex: 5/6/23 ou 05/06/2023
	const match = raw.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
	if (match) {
		const [_, dia, mes, anoRaw] = match;
		const ano = anoRaw.length === 2 ? `20${anoRaw}` : anoRaw; // força 4 dígitos
		const diaPadded = dia.padStart(2, "0");
		const mesPadded = mes.padStart(2, "0");
		const final = `${ano}-${mesPadded}-${diaPadded}`;

		const parsed = new Date(`${final}T00:00:00`);
		if (!isNaN(parsed.getTime())) {
			return { value: final, status: "valid", message: "" };
		}
	}

	return {
		value: raw,
		status: "invalid",
		message: "Data inválida (formato incorreto)",
	};
};

export const isValueMissing = (value: string): boolean => {
	if (!value) return true;
	const str = String(value).trim().toLowerCase();
	return str === "não tem" || str === "-" || str === "";
};

export const fieldValidators: Record<
	string,
	{
		aliases: string[];
		validators: ((value: string) => ValidationResult)[];
	}
> = {
	cpf: {
		aliases: ["cpf", "documento", "cnpj"],
		validators: [normalizeCpfCnpj],
	},
	email: {
		aliases: ["email"],
		validators: [validateEmail],
	},
	cnpjimobiliaria:{
		aliases: ["cnpj imobiliária", "cnpj da imobiliária", "cnpj imobiliaria"],
		validators: [normalizeCpfCnpj],
	},
	cep: {
		aliases: ["cep", "código postal"],
		validators: [validateCep],
	},
	telefone: {
		aliases: ["telefone", "celular", "contato"],
		validators: [validatePhone],
	},
	iniciogarantia: {
		aliases: ["iniciodagarantia", "data de início da garantia"],
		validators: [validateDate],
	},
	fimgarantia: {
		aliases: ["fimdagarantia", "data de fim da garantia"],
		validators: [validateDate],
	},
};
